import axios, { AxiosError, AxiosResponse } from 'axios'

function sanitizeEnv(value: string | undefined): string {
  if (!value) {
    return ''
  }

  const normalized = value.trim()
  if (normalized === 'undefined' || normalized === 'null') {
    return ''
  }

  return normalized
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

function normalizeBasePath(value: string): string {
  const cleaned = value.replace(/^\/+|\/+$/g, '')
  return cleaned ? `/${cleaned}` : ''
}

function isSlacLocalHost(hostname: string): boolean {
  return hostname === 'slac.local' || hostname.endsWith('.slac.local')
}

function isSlacProductionHost(hostname: string): boolean {
  return hostname === 'slac.com.br' || hostname.endsWith('.slac.com.br')
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

function enforceHttpsWhenSecurePage(origin: string, location?: Location): string {
  if (!location || location.protocol !== 'https:' || !origin.startsWith('http://')) {
    return origin
  }

  try {
    const parsed = new URL(origin)
    if (isLoopbackHost(parsed.hostname)) {
      return origin
    }

    parsed.protocol = 'https:'
    return trimTrailingSlash(parsed.toString())
  } catch {
    return origin.replace(/^http:\/\//i, 'https://')
  }
}

function resolveApiBaseUrl(): string {
  const envApiUrl = sanitizeEnv(import.meta.env.VITE_API_URL)
  const envApiBasePath = sanitizeEnv(import.meta.env.VITE_API_BASE_PATH)

  const location = typeof window !== 'undefined' ? window.location : undefined
  const defaultApiOrigin = location
    ? (
        isSlacLocalHost(location.hostname)
          ? `${location.protocol}//api.slac.local`
          : isSlacProductionHost(location.hostname)
            ? `${location.protocol}//api-v2.slac.com.br`
            : 'http://localhost:8002'
      )
    : 'http://localhost:8002'

  const origin = enforceHttpsWhenSecurePage(
    trimTrailingSlash(envApiUrl || defaultApiOrigin),
    location
  )
  const explicitBasePath = normalizeBasePath(envApiBasePath)

  let basePath = explicitBasePath
  if (basePath && origin.endsWith(basePath)) {
    basePath = ''
  }

  if (!basePath && !/\/v\d+$/i.test(origin)) {
    basePath = '/v2'
  }

  return `${origin}${basePath}`
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('customer_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - unwrap Axios response to get API response
api.interceptors.response.use(
  <T = any>(response: AxiosResponse<T>) => response.data as T,
  (error: AxiosError<ApiErrorResponse>) => {
    const apiError: ApiError = {
      message: error.response?.data?.error?.message || 'Erro inesperado. Tente novamente.',
      code: error.response?.data?.error?.code || 'UNKNOWN_ERROR',
      status: error.response?.status || 500,
      details: error.response?.data?.error?.details,
    }

    return Promise.reject(apiError)
  }
)

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
    details?: any
  }
}

export interface ApiErrorResponse {
  success: false
  error: {
    code: string
    message: string
    details?: any
  }
}

export interface ApiError {
  message: string
  code: string
  status: number
  details?: any
}

/**
 * Helper to extract data from ApiResponse in a type-safe way
 */
export function extractData<T>(response: ApiResponse<T>): T {
  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Resposta inválida da API')
  }
  return response.data
}

export const apiClient = {
  get<T = any>(url: string, config?: any): Promise<T> {
    return api.get(url, config) as unknown as Promise<T>
  },
  post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return api.post(url, data, config) as unknown as Promise<T>
  },
  put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return api.put(url, data, config) as unknown as Promise<T>
  },
  delete<T = any>(url: string, config?: any): Promise<T> {
    return api.delete(url, config) as unknown as Promise<T>
  },
}

export { api }
